import { ArrowRightLeft } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/shared/ui/Button";
import SimpleAllocationsList from "./SimpleAllocationsList";
import StockAllocationsList from "./StockAllocationsList";
import { formatAmount, getAllocationCategory } from "./utils";
import type { EditableAllocationState, OperationTypeNature } from "./utils";
import type { LedgerAllocation, LedgerAllocationOperationType } from "../../types/accounting.types";

type AllocationsListProps = {
  allocations: LedgerAllocation[]
  isLoading: boolean
  getState: (allocation: LedgerAllocation) => EditableAllocationState
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isAllocationDirty: (allocation: LedgerAllocation) => boolean
  onSave: (allocation: LedgerAllocation) => void
  onDelete: (id: string) => void
  isBusy: boolean
  // Operation type popover
  operationPopoverAllocationId: string | null
  setOperationPopoverAllocationId: (id: string | null) => void
  operationPopoverSearchByAllocationId: Record<string, string>
  setOperationPopoverSearchByAllocationId: React.Dispatch<React.SetStateAction<Record<string, string>>>
  operationSearchBusyByAllocationId: Record<string, boolean>
  markOperationSearchBusy: (allocationId: string) => void
  operationTypesByBudget: Record<"ASC" | "AEP", LedgerAllocationOperationType[]>
  onFindOrCreateOperationType: (allocation: LedgerAllocation, forcedQuery?: string, forcedNature?: OperationTypeNature) => void
  isCreateOperationTypePending: boolean
  // Editors
  onOpenSimpleEditor: (allocationId: string) => void
  onOpenReimbursementEditor: (allocationId: string) => void
  onOpenEcommerceEditor: (allocationId: string) => void
  onOpenLegacyEditor: (allocationId: string) => void
  onOpenStockEditor: (allocationId: string) => void
  // Busy indicators
  isCreatePending: boolean
  deletingAllocationId: string | null
  // Totals
  totalDebit: number
  totalCredit: number
  totalToAllocate: number
  remainingToAllocate: number
  // Selection mode
  isSelectionMode?: boolean
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onToggleSelectAll?: () => void
  selectedDebit?: number
  selectedCredit?: number
  onOpenMoveDialog?: () => void
  onClearSelection?: () => void
  highlightedAllocationId?: string | null
};

const AllocationsList = ({
  allocations,
  isLoading,
  getState,
  setField,
  isAllocationDirty,
  onSave,
  onDelete,
  isBusy,
  operationPopoverAllocationId,
  setOperationPopoverAllocationId,
  operationPopoverSearchByAllocationId,
  setOperationPopoverSearchByAllocationId,
  operationSearchBusyByAllocationId,
  markOperationSearchBusy,
  operationTypesByBudget,
  onFindOrCreateOperationType,
  isCreateOperationTypePending,
  onOpenSimpleEditor,
  onOpenReimbursementEditor,
  onOpenEcommerceEditor,
  onOpenLegacyEditor,
  onOpenStockEditor,
  isCreatePending,
  deletingAllocationId,
  totalToAllocate,
  remainingToAllocate,
  isSelectionMode = false,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  selectedDebit = 0,
  selectedCredit = 0,
  onOpenMoveDialog,
  onClearSelection,
  highlightedAllocationId,
}: AllocationsListProps) => {
  const stockAllocations = useMemo(
    () => allocations.filter((a) => getAllocationCategory(a) === "stock"),
    [allocations],
  );
  const simpleAllocations = useMemo(
    () => allocations.filter((a) => getAllocationCategory(a) !== "stock"),
    [allocations],
  );

  const simpleTotalDebit = useMemo(() => simpleAllocations.reduce((acc, a) => acc + (a.debit ?? 0), 0), [simpleAllocations]);
  const simpleTotalCredit = useMemo(() => simpleAllocations.reduce((acc, a) => acc + (a.credit ?? 0), 0), [simpleAllocations]);

  const allLocked = allocations.length > 0 && allocations.every((a) => a.locked);
  const hasStockAllocations = stockAllocations.length > 0;
  const hasSimpleAllocations = simpleAllocations.length > 0;

  return (
    <>
      {hasStockAllocations && (
        <StockAllocationsList
          allocations={stockAllocations}
          getState={getState}
          setField={setField}
          isAllocationDirty={isAllocationDirty}
          onSave={onSave}
          onDelete={onDelete}
          isBusy={isBusy}
          onOpenStockEditor={onOpenStockEditor}
          deletingAllocationId={deletingAllocationId}
          isSelectionMode={isSelectionMode}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          highlightedAllocationId={highlightedAllocationId}
        />
      )}

      {hasStockAllocations && (
        <div className="mt-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
          Les écritures stock permettent de créditer ou débiter des unités d&apos;une référence en stock.
          Elles doivent généralement être complétées par une écriture simple pour qualifier la partie financière du flux.
        </div>
      )}

      {hasStockAllocations && (hasSimpleAllocations || isLoading) && (
        <div className="h-3" />
      )}

      {(hasSimpleAllocations || isLoading || !hasStockAllocations) && (
        <SimpleAllocationsList
          allocations={simpleAllocations}
          isLoading={isLoading}
          getState={getState}
          setField={setField}
          isAllocationDirty={isAllocationDirty}
          onSave={onSave}
          onDelete={onDelete}
          isBusy={isBusy}
          operationPopoverAllocationId={operationPopoverAllocationId}
          setOperationPopoverAllocationId={setOperationPopoverAllocationId}
          operationPopoverSearchByAllocationId={operationPopoverSearchByAllocationId}
          setOperationPopoverSearchByAllocationId={setOperationPopoverSearchByAllocationId}
          operationSearchBusyByAllocationId={operationSearchBusyByAllocationId}
          markOperationSearchBusy={markOperationSearchBusy}
          operationTypesByBudget={operationTypesByBudget}
          onFindOrCreateOperationType={onFindOrCreateOperationType}
          isCreateOperationTypePending={isCreateOperationTypePending}
          onOpenSimpleEditor={onOpenSimpleEditor}
          onOpenReimbursementEditor={onOpenReimbursementEditor}
          onOpenEcommerceEditor={onOpenEcommerceEditor}
          onOpenLegacyEditor={onOpenLegacyEditor}
          isCreatePending={isCreatePending}
          deletingAllocationId={deletingAllocationId}
          totalDebit={simpleTotalDebit}
          totalCredit={simpleTotalCredit}
          isSelectionMode={isSelectionMode}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={onToggleSelectAll}
          highlightedAllocationId={highlightedAllocationId}
        />
      )}

      {!allLocked && hasSimpleAllocations && (
        <>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-2 text-[11px]">
            <div className="text-slate-600 flex items-center">Total à affecter</div>
            <div className="flex items-center rounded border bg-slate-50 h-7 px-2">
              <span className="font-semibold tabular-nums">{formatAmount(totalToAllocate)}</span>
              <span className="ml-1 text-slate-500">€</span>
            </div>
            <div className="text-slate-600 flex items-center">Reste à affecter</div>
            <div className="flex items-center rounded border bg-slate-50 h-7 px-2">
              <span className="font-semibold tabular-nums">{formatAmount(remainingToAllocate)}</span>
              <span className="ml-1 text-slate-500">€</span>
            </div>
          </div>

          <div className="mt-2 text-[11px] text-slate-500">
            {simpleAllocations.length}
            {" "}
            écriture(s)
          </div>
        </>
      )}

      {isSelectionMode && selectedIds && selectedIds.size > 0 && (
        <div className="mt-3 flex items-center gap-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2">
          <span className="text-[11px] font-medium text-indigo-800">
            {selectedIds.size}
            {" "}
            écriture(s) sélectionnée(s)
          </span>
          <span className="text-[11px] text-indigo-600 tabular-nums">
            D :
            {" "}
            {formatAmount(selectedDebit)}
            {" "}
            € / C :
            {" "}
            {formatAmount(selectedCredit)}
            {" "}
            €
          </span>
          <div className="flex-1" />
          <button
            type="button"
            className="text-[11px] text-indigo-600 hover:text-indigo-800 underline"
            onClick={onClearSelection}
          >
            Désélectionner
          </button>
          <Button
            type="button"
            size="sm"
            className="h-7 px-2.5 text-[11px] gap-1.5"
            onClick={onOpenMoveDialog}
          >
            <ArrowRightLeft className="h-3 w-3" />
            Déplacer vers...
          </Button>
        </div>
      )}
    </>
  );
};

export default AllocationsList;
